import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { FileSignature, LayoutDashboard, FolderOpen, ShieldCheck, LogOut, Menu, X, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/generate", label: "Nouveau Contrat", icon: FileSignature, testid: "nav-link-generate" },
  { to: "/contracts", label: "Anciens Contrats", icon: FolderOpen, testid: "nav-link-contracts" },
  { to: "/owners", label: "Propriétaires", icon: Building2, testid: "nav-link-owners" },
  { to: "/", label: "Tableau de Bord", icon: LayoutDashboard, testid: "nav-link-dashboard", end: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 ${
      isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  const allLinks = user?.role === "admin"
    ? [...links, { to: "/admin", label: "Administration", icon: ShieldCheck, testid: "nav-link-admin" }]
    : links;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="bg-slate-900 text-white p-2 rounded-lg">
              <FileSignature className="h-5 w-5" />
            </span>
            <span className="font-extrabold tracking-tight text-slate-900 text-lg hidden sm:block">
              ContratExpress <span className="text-blue-600">Pro</span>
            </span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1">
            {allLinks.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink key={to} to={to} end={end} className={navClass} data-testid={testid}>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-semibold text-slate-900" data-testid="user-name">{user?.name}</span>
              <span className="text-xs text-slate-500 uppercase tracking-wider" data-testid="user-role">
                {user?.role === "admin" ? "Administrateur" : "Agent"}
              </span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-150"
              title="Déconnexion"
            >
              <LogOut className="h-5 w-5" />
            </button>
            <button
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1">
            {allLinks.map(({ to, label, icon: Icon, testid, end }) => (
              <NavLink key={to} to={to} end={end} className={navClass} data-testid={`${testid}-mobile`} onClick={() => setMenuOpen(false)}>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
